import React, { useEffect, useRef } from "react";
import { drawPowerUpAnim, POWERUP_ANIMS } from "../../game-canvas/powerup-anim";
import { getPowerUpImage } from "../../game-canvas/renderers/powerup-image-loader.js";

// Dev-only gallery of power-up SPAWN + DE-SPAWN (grab) animations. Each tile loops a full
// lifecycle: spawn -> idle -> grab -> gap -> repeat, on the current token look. Route: /pa
const TILE_W = 300;
const TILE_H = 150;
const R = 30; // token radius px
const ICON = "speed"; // a green (self) sample

// lifecycle timing (seconds)
const T_SPAWN = 0.4, T_IDLE = 1.0, T_GRAB = 0.5, T_GAP = 0.5;
const TOTAL = T_SPAWN + T_IDLE + T_GRAB + T_GAP;

export default function PowerUpAnimDemo() {
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvasRefs.current.forEach((c) => { if (c) { c.width = TILE_W * dpr; c.height = TILE_H * dpr; } });
    const img = getPowerUpImage(ICON);
    const imgRed = getPowerUpImage("slow"); // a red (others) sample for the directional tiles
    const marker = (ctx: CanvasRenderingContext2D, p: { x: number; y: number }, color: string) => {
      ctx.save(); ctx.globalAlpha = 0.5; ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(p.x, p.y, 5, 0, Math.PI * 2); ctx.fill(); ctx.restore();
    };

    function loop(now: number) {
      if (!startRef.current) startRef.current = now;
      const t = (now - startRef.current) / 1000;
      POWERUP_ANIMS.forEach((v, idx) => {
        const c = canvasRefs.current[idx];
        const ctx = c && c.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, TILE_W, TILE_H);
        // stagger each tile slightly so the grid feels alive
        const lt = (t + idx * 0.13) % TOTAL;
        let s = 1, d = 0;
        if (lt < T_SPAWN) { s = lt / T_SPAWN; }
        else if (lt < T_SPAWN + T_IDLE) { s = 1; }
        else if (lt < T_SPAWN + T_IDLE + T_GRAB) { s = 1; d = (lt - T_SPAWN - T_IDLE) / T_GRAB; }
        else { d = 1; } // gap: gone
        if (d >= 1) return; // nothing to draw
        if (v.n >= 41) {
          // Directional: green (left) sucked toward a worm; red (right) thrown at two worms.
          const gT = { x: 52, y: 40 };
          marker(ctx, gT, "#22c55e");
          drawPowerUpAnim(ctx, 108, TILE_H / 2, R * 0.8, img, "self", v.n, t, s, d, { selfTarget: gT });
          const eT = [{ x: 238, y: 98 }, { x: 289, y: 30 }]; // one near, one far (shows distance-timed landing)
          eT.forEach((e) => marker(ctx, e, "#ff3b52"));
          drawPowerUpAnim(ctx, 200, TILE_H / 2, R * 0.8, imgRed, "others", v.n, t, s, d, { enemyTargets: eT });
        } else {
          drawPowerUpAnim(ctx, TILE_W / 2, TILE_H / 2, R, img, "self", v.n, t, s, d);
        }
      });
      rafRef.current = window.requestAnimationFrame(loop);
    }
    rafRef.current = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "8px 16px 60px", fontFamily: "'Rajdhani', sans-serif", color: "#eaf0ff" }}>
      <h1 style={{ fontFamily: "'Orbitron', sans-serif", letterSpacing: 2, fontSize: 22 }}>Power-up spawn / grab animations</h1>
      <p style={{ color: "#8995b3", fontSize: 14, marginTop: 4 }}>
        How a power-up appears and how it leaves when grabbed. Each tile loops spawn, a beat, then the
        grab. Uses the current token look. Switch it live in a match with <b>?pa=1..69</b>. 41+ are
        directional (green sucked into the grabber, red homes in on rivals); 54+ are missiles/projectiles
        whose travel time scales with distance, landing with an impact. 69 is the combo (missile red /
        dust green+blue) and the default.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14, marginTop: 16 }}>
        {POWERUP_ANIMS.map((v, idx) => (
          <div key={v.n} style={{ background: "#0b0f17", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "8px 11px" }}>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, color: "#37c8e0", fontSize: 15 }}>{v.n}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "#c3ccdf" }}>{v.label}</span>
            </div>
            <canvas ref={(el) => { canvasRefs.current[idx] = el; }} style={{ width: TILE_W, height: TILE_H, display: "block", background: "radial-gradient(120% 100% at 50% 40%, #131b2e 0%, #0a0e16 100%)" }} />
          </div>
        ))}
      </div>
    </div>
  );
}
